import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'
import { errorMessage } from '@/lib/utils'

/**
 * App Patch Policy. `GET /admin/app-patch-policy` is just a per-platform "what's live on stable"
 * summary now (see PatchPolicyItem below) — the update-dialog message itself moved to being
 * scoped per (platform, release_version, patch_number), same idea as MandatoryRule already was
 * one level coarser: Shorebird patch numbers restart at 1 per release, so patch1 and patch2 on
 * the same release_version are two independently-promoted patches, and a message set for one must
 * never leak into the other. cwc-backend-v1's public `GET /api/v1/app-patch-status?platform=`
 * reads app_patch_policy to drive that force/optional-update dialog in the Flutter app.
 */

export const PLATFORMS = ['ios', 'android'] as const
export type Platform = (typeof PLATFORMS)[number]

export const PLATFORM_LABEL: Record<Platform, string> = {
  ios: 'iOS',
  android: 'Android',
}

export interface PatchPolicyItem {
  platform: Platform
  // From the shorebird_latest_stable_patch cache (see service.SyncLatestStablePatch), not staff
  // set: this platform's highest synced release_version, and that release's patch number/size.
  // Null until this platform has been synced once (a promote/rollback/rollforward, or the
  // mandatory-rules tab). Read-only, display-only — feeds an *optional* update's real
  // download-progress size on mobile.
  latest_release_version: string | null
  latest_patch_number: number | null
  latest_patch_size_bytes: number | null
}

const KEY = ['app-patch-policy']

// release_version travels as a query param (not a path segment) since it can contain a literal
// "+" (e.g. "1.0.1+6").
function releaseQuery(releaseVersion: string) {
  return `release_version=${encodeURIComponent(releaseVersion)}`
}

function releaseScopeQuery(releaseVersion: string, patchNumber: number) {
  return `${releaseQuery(releaseVersion)}&patch_number=${patchNumber}`
}

function stablePatchesKey(platform: Platform | null) {
  return ['shorebird-stable-patches', platform]
}

export function useAppPatchPolicy() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<PatchPolicyItem[]>('/admin/app-patch-policy'),
  })
}

/**
 * One exact (platform, release_version, patch_number)'s update-dialog message — the force-update
 * screen's heading plus the message shown in either dialog. Scoped this tightly so a message set
 * while promoting patch1 never leaks into patch2 just because a later promote left the field
 * blank; "blank means keep whatever this exact patch already has" is resolved by the console
 * itself (it fetches this via usePatchMessage before ever letting staff submit), not by the
 * server — see useSavePatchMessage below.
 */
export const patchMessageInputSchema = z.object({
  // The force-update screen's heading. Null keeps the mobile app's own built-in, already-localized
  // default title — unlike message below, this is never run through per-language translation, so
  // a value set here ships as one fixed string to every language.
  title: z.string().nullable(),
  // Shown to the user in the force-restart or optional-update dialog.
  message: z.string().nullable(),
})

export type PatchMessageInput = z.infer<typeof patchMessageInputSchema>

export interface PatchMessage extends PatchMessageInput {
  platform: Platform
  release_version: string
  patch_number: number
  updated_by: string | null
  updated_at: string | null
}

function patchMessageKey(platform: Platform, releaseVersion: string, patchNumber: number) {
  return ['app-patch-policy-message', platform, releaseVersion, patchNumber]
}

// enabled only once a real release_version + patch_number are picked — patchNumber === 0 is this
// form's "nothing picked yet" sentinel (see mandatory-rules-card.tsx/promote-panel.tsx), not a
// real Shorebird patch number (those start at 1).
export function usePatchMessage(platform: Platform | null, releaseVersion: string, patchNumber: number) {
  return useQuery({
    queryKey: platform ? patchMessageKey(platform, releaseVersion, patchNumber) : ['app-patch-policy-message', null],
    queryFn: () =>
      api.get<PatchMessage | null>(
        `/admin/app-patch-policy/${platform}/message?${releaseScopeQuery(releaseVersion, patchNumber)}`,
      ),
    enabled: platform !== null && releaseVersion.trim() !== '' && patchNumber > 0,
  })
}

function putPatchMessage(platform: Platform, releaseVersion: string, patchNumber: number, input: PatchMessageInput) {
  return api.put<PatchMessage>(
    `/admin/app-patch-policy/${platform}/message?${releaseScopeQuery(releaseVersion, patchNumber)}`,
    input,
  )
}

// "Blank means keep whatever this exact patch already has" — trims both fields and falls back to
// current's value (never the empty string) so a partial edit never silently nulls out the other.
export function mergePatchMessage(current: PatchMessage | null | undefined, title: string, message: string): PatchMessageInput {
  const trimmedTitle = title.trim()
  const trimmedMessage = message.trim()
  return {
    title: trimmedTitle !== '' ? trimmedTitle : (current?.title ?? null),
    message: trimmedMessage !== '' ? trimmedMessage : (current?.message ?? null),
  }
}

export function useSavePatchMessage() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      platform,
      release_version,
      patch_number,
      input,
    }: {
      platform: Platform
      release_version: string
      patch_number: number
      input: PatchMessageInput
    }) => putPatchMessage(platform, release_version, patch_number, input),
    onSuccess: (saved) =>
      queryClient.setQueryData<PatchMessage>(
        patchMessageKey(saved.platform, saved.release_version, saved.patch_number),
        saved,
      ),
  })
}

/**
 * The mandatory Shorebird patch floor — scoped per platform+release_version, a separate table
 * from the per-patch message above (see PatchMessage's own doc). Shorebird patch numbers restart
 * at 1 per release, so a floor calibrated for one release_version is meaningless (or actively
 * dangerous — an unclosable restart loop on any device that reports a different one) applied
 * anywhere else. Any number of rules can be active for a platform at once, one per release_version
 * that currently needs a floor — e.g. one still forcing stragglers on an old release onto its own
 * patch number, alongside a separate one for whatever release is current now.
 */
export interface MandatoryRule {
  platform: Platform
  release_version: string
  patch_number: number
  // Resolved server-side from Shorebird's own API, never a form field — see
  // service.SaveMandatoryRule in cwc-admin-v1. Null if it couldn't be resolved.
  patch_size_bytes: number | null
  updated_by: string | null
  updated_at: string | null
  // True when the save skipped the is-it-live check because Shorebird is disabled on this
  // environment: the floor was stored without ever being verified as reachable. Save response
  // only, so a later list read never carries it.
  unverified?: boolean
}

// Shared shape for every route targeting one exact patch. patch_number >= 1: 0 is the forms'
// "nothing picked yet" sentinel, and real Shorebird patch numbers start at 1.
export const patchTargetSchema = z.object({
  platform: z.enum(PLATFORMS),
  release_version: z.string().trim().min(1, 'Release version is required.'),
  patch_number: z.number().int('Whole numbers only.').min(1, 'Pick a real patch number.'),
})

export const mandatoryRuleInputSchema = patchTargetSchema.omit({ platform: true })

export type MandatoryRuleInput = z.infer<typeof mandatoryRuleInputSchema>

function mandatoryRulesKey(platform: Platform) {
  return ['app-patch-mandatory-rules', platform]
}

function sortRules(rules: MandatoryRule[]) {
  return [...rules].sort((a, b) => a.release_version.localeCompare(b.release_version))
}

// Upserts `rule` into its platform's cached list by release_version — shared by every
// mandatory-rule write.
function upsertMandatoryRule(queryClient: QueryClient, platform: Platform, rule: MandatoryRule) {
  queryClient.setQueryData<MandatoryRule[]>(mandatoryRulesKey(platform), (current) =>
    sortRules([...(current?.filter((r) => r.release_version !== rule.release_version) ?? []), rule]),
  )
}

function putMandatoryRule(platform: Platform, releaseVersion: string, patchNumber: number) {
  return api.put<MandatoryRule>(
    `/admin/app-patch-policy/${platform}/mandatory-rules?${releaseQuery(releaseVersion)}`,
    { patch_number: patchNumber },
  )
}

export function useMandatoryRules(platform: Platform) {
  return useQuery({
    queryKey: mandatoryRulesKey(platform),
    queryFn: () => api.get<MandatoryRule[]>(`/admin/app-patch-policy/${platform}/mandatory-rules`),
  })
}

export function useSaveMandatoryRule(platform: Platform) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ release_version, patch_number }: MandatoryRuleInput) =>
      putMandatoryRule(platform, release_version, patch_number),
    onSuccess: (saved) => upsertMandatoryRule(queryClient, platform, saved),
  })
}

export function useDeleteMandatoryRule(platform: Platform) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (releaseVersion: string) =>
      api
        .delete<null>(
          `/admin/app-patch-policy/${platform}/mandatory-rules?${releaseQuery(releaseVersion)}`,
        )
        .then(() => releaseVersion),
    onSuccess: (releaseVersion) =>
      queryClient.setQueryData<MandatoryRule[]>(mandatoryRulesKey(platform), (current) =>
        current?.filter((r) => r.release_version !== releaseVersion),
      ),
  })
}

/**
 * The native app-store-level forced-update gate — entirely separate from MandatoryRule above.
 * That one is a Shorebird patch floor, scoped per release_version, for gaps a code-push patch can
 * fix. This one is a floor on the native binary's own version+build, for when the gap is something
 * a patch never can (new native code, a permission, a store policy requirement, etc.) — the app
 * blocks and sends the user to the Play Store/App Store instead.
 *
 * Unlike the patch floor, this does NOT need per-release_version scoping: Apple/Google both
 * enforce that a store's published version+build only ever increases, so one row per platform is
 * architecturally sufficient — see cwc-admin-v1's app_native_version_policy migration. min_version
 * null means the gate is off for that platform, the default state.
 *
 * No store_url: the mobile app resolves the store destination itself (package name for Android,
 * a fixed App Store ID constant for iOS) — a store URL is a fact about the app binary, not
 * admin-configured data.
 */
// Which of the fixed security/performance/feature marketing bullets to show — always the mobile
// app's own localized copy, never staff-authored text. All three default to true, matching the
// mobile app's pre-existing behavior of always showing all three unconditionally.
export interface Reasons {
  security: boolean
  performance: boolean
  feature: boolean
}

const reasonsSchema = z.object({ security: z.boolean(), performance: z.boolean(), feature: z.boolean() })

export interface NativeVersionPolicy {
  platform: Platform
  min_version: string | null
  min_build_number: number | null
  message: string | null
  title: string | null
  mandatory_reasons: Reasons
  optional_min_version: string | null
  optional_min_build_number: number | null
  optional_title: string | null
  optional_message: string | null
  optional_reasons: Reasons
  updated_by: string | null
  updated_at: string | null
}

export const nativeVersionPolicyInputSchema = z
  .object({
    min_version: z
      .string()
      .trim()
      .regex(/^\d+\.\d+\.\d+$/, 'Must look like X.Y.Z, e.g. 1.2.0.')
      .nullable(),
    min_build_number: z.number().int('Whole numbers only.').min(0, 'Cannot be negative.').nullable(),
    message: z.string().nullable(),
    title: z.string().nullable(),
    mandatory_reasons: reasonsSchema,
    optional_min_version: z
      .string()
      .trim()
      .regex(/^\d+\.\d+\.\d+$/, 'Must look like X.Y.Z, e.g. 1.2.0.')
      .nullable(),
    optional_min_build_number: z.number().int('Whole numbers only.').min(0, 'Cannot be negative.').nullable(),
    optional_title: z.string().nullable(),
    optional_message: z.string().nullable(),
    optional_reasons: reasonsSchema,
  })
  // A build number without a version to pair it with is meaningless, so the schema rejects it
  // too, not just the Clear button's manual reset.
  .refine((value) => value.min_version !== null || value.min_build_number === null, {
    message: 'Clear the minimum version before clearing the build number.',
    path: ['min_build_number'],
  })
  .refine((value) => value.optional_min_version !== null || value.optional_min_build_number === null, {
    message: 'Clear the minimum version before clearing the build number.',
    path: ['optional_min_build_number'],
  })

export type NativeVersionPolicyInput = z.infer<typeof nativeVersionPolicyInputSchema>

function nativeVersionPolicyKey(platform: Platform) {
  return ['app-native-version-policy', platform]
}

export function useNativeVersionPolicy(platform: Platform) {
  return useQuery({
    queryKey: nativeVersionPolicyKey(platform),
    queryFn: () => api.get<NativeVersionPolicy>(`/admin/app-patch-policy/${platform}/native-version`),
  })
}

export function useSaveNativeVersionPolicy(platform: Platform) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NativeVersionPolicyInput) =>
      api.put<NativeVersionPolicy>(`/admin/app-patch-policy/${platform}/native-version`, input),
    onSuccess: (saved) => queryClient.setQueryData<NativeVersionPolicy>(nativeVersionPolicyKey(platform), saved),
  })
}

/**
 * Patch promotion — the real release gate. Engineers publish a Shorebird
 * patch to a non-default channel (e.g. "staging"), invisible to real users
 * because Shorebird's client-side updater only auto-checks "stable" by
 * default. This calls the admin backend, which calls Shorebird's own REST API
 * server-to-server to move one specific patch onto "stable" — no CLI or
 * rebuild needed, and nothing reaches production until a staff member does
 * this.
 *
 * One platform per promotion, because a patch number only ever belongs to one of them: Shorebird
 * numbers patches per release across both platforms, so `shorebird patch -p ios -p android`
 * publishes two patches with consecutive numbers, each shipping artifacts for a single platform.
 * Promoting one number to both would put the wrong (older) change live on the second.
 */
export const promotePatchInputSchema = patchTargetSchema.extend({
  platform: z.enum(PLATFORMS, { message: 'Select a platform.' }),
  // When checked, usePromotePatch also PUTs this same patch_number as the
  // mandatory floor on every platform that promotes successfully — the
  // common case (this patch is both the release gate and the floor) in one
  // submit instead of a separate trip to the platform cards below.
  set_mandatory: z.boolean(),
  // Same app_patch_policy.title column the mandatory-rules card edits, scoped to this exact
  // release_version+patch_number. Blank keeps whatever title this exact patch already has.
  title: z.string().nullable(),
  // Same app_patch_policy.message column, independent of set_mandatory — a non-empty value here
  // saves even when set_mandatory is left unchecked; blank leaves this exact patch's message as-is.
  message: z.string().nullable(),
})

export type PromotePatchInput = z.infer<typeof promotePatchInputSchema>

// The actual wire shape of one POST /admin/app-patch-policy/promote call —
// one platform, matching the Go handler's PromoteInput. Exported so the mock
// handler validates against what a real request looks like, not the
// multi-platform form schema above.
export const promotePatchRequestSchema = patchTargetSchema

export type PromotePatchApiResult = {
  platform: Platform
  release_version: string
  patch_number: number
  release_id: number
  patch_id: number
  previous_channel: string
  promoted_to: string
  already_promoted: boolean
  // Set only by the rollback endpoint (never promote): every patch number, newer than
  // patch_number on this release, that this call had to mark rolled back (via Shorebird's own
  // rollback endpoint) so patch_number would actually become what's served again — see
  // service.RollbackToPatch in cwc-admin-v1. Empty when there was nothing newer to roll back.
  rolled_back_patch_numbers: number[]
  // Set only by the rollback endpoint (never promote), and only when this platform's rule for
  // this exact release_version was above patch_number and had to be lowered to match — see
  // service.LowerMandatoryFloorIfUnreachable in cwc-admin-v1. Null means nothing needed fixing.
  policy_adjusted: MandatoryRule | null
}

// One outcome per platform submitted. `error` is set instead of `result` when that platform's
// promote call failed, so one platform's failure renders next to the other platform's success
// rather than being lost. `messageSaved`/`messageError` and `ruleSaved`/`ruleError` are each only
// present when there was actually a write to do — a non-empty message was typed (independent of
// set_mandatory), or set_mandatory was checked — and that platform's promote succeeded first.
export interface PromotePatchOutcome {
  platform: Platform
  result?: PromotePatchApiResult
  error?: string
  messageSaved?: PatchMessage
  messageError?: string
  ruleSaved?: MandatoryRule
  ruleError?: string
}

// Invalidate, never refetch, stable patches: an immediate re-read sees Shorebird's pre-write state and
// its full sync would overwrite the row the server just wrote (promote/roll already refresh it).
async function refreshAfterChannelChange(queryClient: QueryClient, platform: Platform, { rules }: { rules: boolean }) {
  await queryClient.invalidateQueries({ queryKey: stablePatchesKey(platform) })
  await queryClient.invalidateQueries({ queryKey: KEY })
  if (rules) await queryClient.invalidateQueries({ queryKey: mandatoryRulesKey(platform) })
}

export function usePromotePatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      platform,
      release_version,
      patch_number,
      set_mandatory,
      title,
      message,
    }: PromotePatchInput): Promise<PromotePatchOutcome> => {
      let result: PromotePatchApiResult
      try {
        result = await api.post<PromotePatchApiResult>('/admin/app-patch-policy/promote', {
          platform,
          release_version,
          patch_number,
        })
      } catch (err) {
        return { platform, error: errorMessage(err, 'Promotion failed.') }
      }

      const outcome: PromotePatchOutcome = { platform, result }

      // Only reached once the promote above actually succeeded. Independent of set_mandatory
      // below — a non-empty title/message here is saved regardless of whether this promote is
      // also becoming the mandatory floor. Blank leaves whatever this exact
      // platform+release_version+patch_number already has untouched — resolved here against
      // whatever the console last fetched for this exact patch (see usePatchMessage), not a
      // server-side merge (see PatchMessageInput's doc).
      const trimmedTitle = title?.trim() ?? ''
      const trimmedMessage = message?.trim() ?? ''
      if (trimmedTitle !== '' || trimmedMessage !== '') {
        try {
          const current = queryClient.getQueryData<PatchMessage | null>(
            patchMessageKey(platform, release_version, patch_number),
          )
          const saved = await putPatchMessage(
            platform,
            release_version,
            patch_number,
            mergePatchMessage(current, title ?? '', message ?? ''),
          )
          queryClient.setQueryData<PatchMessage>(patchMessageKey(platform, release_version, patch_number), saved)
          outcome.messageSaved = saved
        } catch (err) {
          outcome.messageError = errorMessage(err, 'Failed to save message.')
        }
      }

      if (set_mandatory) {
        try {
          const rule = await putMandatoryRule(platform, release_version, patch_number)
          upsertMandatoryRule(queryClient, platform, rule)
          outcome.ruleSaved = rule
        } catch (err) {
          outcome.ruleError = errorMessage(err, 'Failed to save mandatory rule.')
        }
      }

      return outcome
    },
    onSuccess: (_result, { platform }) => refreshAfterChannelChange(queryClient, platform, { rules: true }),
  })
}

/**
 * Rollback — the emergency counterpart to promote, for when a patch already live on stable turns
 * out to have a problem. NOT mechanically identical to promote: a channel tag alone doesn't stop a
 * newer, still-tagged patch from winning (Shorebird resolves "newest non-rolled-back patch on this
 * channel"), so the backend (service.RollbackToPatch) marks every newer patch on this release as
 * rolled back via Shorebird's own rollback endpoint, then (re)promotes the target if it wasn't
 * already on stable. Hits its own route so the audit trail records "shorebird_patch_rolled_back"
 * instead of "shorebird_patch_promoted".
 */
export const rollbackPatchInputSchema = patchTargetSchema

export type RollbackPatchInput = z.infer<typeof rollbackPatchInputSchema>

export function useRollbackPatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RollbackPatchInput) =>
      api.post<PromotePatchApiResult>('/admin/app-patch-policy/rollback', input),
    onSuccess: async (result, { platform }) => {
      // Rollback can silently lower this release_version's mandatory rule server-side
      // (policy_adjusted) — see service.LowerMandatoryFloorIfUnreachable in cwc-admin-v1. Patch
      // the mandatory-rules cache in place immediately so it reflects that without waiting on the
      // invalidation below, same pattern as useSaveMandatoryRule.
      if (result.policy_adjusted) {
        upsertMandatoryRule(queryClient, platform, result.policy_adjusted)
      }
      await refreshAfterChannelChange(queryClient, platform, { rules: true })
    },
  })
}

/**
 * Rollforward — undoes a Rollback: reactivates a patch that was previously marked rolled back
 * (service.RollforwardPatch), via Shorebird's own rollforward endpoint. No mandatory-floor side
 * effect the way Rollback has (rolling a patch forward doesn't change what any mandatory rule
 * requires), so no policy_adjusted patching here.
 */
export type RollforwardResult = {
  platform: Platform
  release_version: string
  patch_number: number
  release_id: number
  patch_id: number
  // True when the patch was not rolled back to begin with — an idempotent success rather than a
  // fresh roll-forward.
  already_active: boolean
}

export function useRollforwardPatch() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RollbackPatchInput) =>
      api.post<RollforwardResult>('/admin/app-patch-policy/rollforward', input),
    onSuccess: (_result, { platform }) => refreshAfterChannelChange(queryClient, platform, { rules: false }),
  })
}

/**
 * Release/patch pickers for the promote form — read-only lookups against
 * Shorebird itself (via the admin backend), so staff choose a
 * release_version and patch_number from what actually exists instead of
 * retyping them from memory and finding out about a typo only after
 * submitting.
 */
export interface ShorebirdRelease {
  id: number
  version: string
  // This release's status for the platform that was asked for (e.g.
  // "active"/"draft"), already filtered server-side — never the whole
  // per-platform map.
  status: string
  created_at: string
}

export interface ShorebirdReleasePatch {
  id: number
  number: number
  channel: string
  is_rolled_back: boolean
}

export function useShorebirdReleases(platform: Platform | null) {
  return useQuery({
    queryKey: ['shorebird-releases', platform],
    queryFn: () =>
      api.get<ShorebirdRelease[]>(`/admin/app-patch-policy/releases?platform=${platform}`),
    // No platform picked yet (or nothing checked in the form) — there is
    // nothing sane to list, so don't fire a request that would 400.
    enabled: platform !== null,
  })
}

/**
 * Per-language translations of one exact patch's English message (the `message` field on
 * PatchMessage — that stays the baseline, never touched here). Small dedicated table +
 * translator, not the full app/i18n catalog: see cwc-admin-v1's create_app_patch_policy_message.sql
 * for why. A language with no saved row yet still appears (content null) so the console shows
 * every enabled language, not only the ones a translate run happened to reach.
 */
export interface MessageDetail {
  language_code: string
  display_name: string
  // The force-update screen's heading in this language. Shares is_manual/needs_review with
  // content — a row is reviewed as a whole, not field by field.
  title: string | null
  content: string | null
  is_manual: boolean
  needs_review: boolean
  updated_by: string | null
  updated_at: string | null
}

function messageDetailsKey(platform: Platform, releaseVersion: string, patchNumber: number) {
  return ['app-patch-policy-messages', platform, releaseVersion, patchNumber]
}

export function useMessageDetails(platform: Platform, releaseVersion: string, patchNumber: number) {
  return useQuery({
    queryKey: messageDetailsKey(platform, releaseVersion, patchNumber),
    queryFn: () =>
      api.get<MessageDetail[]>(`/admin/app-patch-policy/${platform}/messages?${releaseScopeQuery(releaseVersion, patchNumber)}`),
    enabled: releaseVersion.trim() !== '' && patchNumber > 0,
  })
}

/**
 * A person directly editing one language — always lands as is_manual.
 * language_code travels in the body, not the URL — one path param
 * (:platform) is enough for the router, and keeps this mockable with a
 * single capture group; the i18n module's own /messages/details endpoint
 * uses this same body-carries-the-second-key shape.
 */
export function useSaveMessageDetail(platform: Platform, releaseVersion: string, patchNumber: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      languageCode,
      title,
      content,
    }: {
      languageCode: string
      // Blank keeps whatever heading the row already has, so approving a message-only draft
      // never wipes a title translated earlier.
      title: string
      content: string
    }) =>
      api.put<MessageDetail>(`/admin/app-patch-policy/${platform}/messages?${releaseScopeQuery(releaseVersion, patchNumber)}`, {
        language_code: languageCode,
        title,
        content,
      }),
    onSuccess: (saved) =>
      queryClient.setQueryData<MessageDetail[]>(messageDetailsKey(platform, releaseVersion, patchNumber), (current) =>
        current?.map((item) => (item.language_code === saved.language_code ? saved : item)),
      ),
  })
}

/**
 * Regenerates one language's draft from the current English message.
 * Synchronous on the wire (unlike app/i18n's fire-and-forget + poll): this is
 * one provider call, not a fan-out, so the console can just await it.
 */
export function useRetranslateMessageDetail(platform: Platform, releaseVersion: string, patchNumber: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (languageCode: string) =>
      api.post<MessageDetail>(`/admin/app-patch-policy/${platform}/messages/retranslate?${releaseScopeQuery(releaseVersion, patchNumber)}`, {
        language_code: languageCode,
      }),
    onSuccess: (saved) =>
      queryClient.setQueryData<MessageDetail[]>(messageDetailsKey(platform, releaseVersion, patchNumber), (current) =>
        current?.map((item) => (item.language_code === saved.language_code ? saved : item)),
      ),
  })
}

/**
 * Fans the still-unsaved English textarea content out for translation —
 * before Save is ever clicked on it. Fire-and-forget on the wire (the
 * backend queues it and returns immediately); nothing in the response is
 * used, so there is nothing to merge into the cache here — reopening the
 * Translations dialog a moment later picks up the fresh drafts because it
 * only ever mounts (and fetches) while actually open.
 */
export function useTranslateDraft(platform: Platform, releaseVersion: string, patchNumber: number) {
  return useMutation({
    mutationFn: ({ title, message }: { title: string; message: string }) =>
      api.post<{ queued: boolean }>(
        `/admin/app-patch-policy/${platform}/messages/translate-draft?${releaseScopeQuery(releaseVersion, patchNumber)}`,
        { title, message },
      ),
  })
}

export const VARIANTS = ['mandatory', 'optional'] as const
export type Variant = (typeof VARIANTS)[number]

/** Same shape as MessageDetail, for the native version gate's title/message (mandatory) or
 * optional_title/optional_message (optional) instead of a Shorebird patch's. */
export interface NativeMessageDetail {
  language_code: string
  display_name: string
  title: string | null
  content: string | null
  is_manual: boolean
  needs_review: boolean
  updated_by: string | null
  updated_at: string | null
}

function nativeMessageDetailsKey(platform: Platform, variant: Variant) {
  return ['app-native-version-policy-messages', platform, variant]
}

export function useNativeMessageDetails(platform: Platform, variant: Variant) {
  return useQuery({
    queryKey: nativeMessageDetailsKey(platform, variant),
    queryFn: () =>
      api.get<NativeMessageDetail[]>(`/admin/app-patch-policy/${platform}/native-version/messages?variant=${variant}`),
  })
}

export function useSaveNativeMessageDetail(platform: Platform, variant: Variant) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ languageCode, title, content }: { languageCode: string; title: string; content: string }) =>
      api.put<NativeMessageDetail>(`/admin/app-patch-policy/${platform}/native-version/messages?variant=${variant}`, {
        language_code: languageCode,
        title,
        content,
      }),
    onSuccess: (saved) =>
      queryClient.setQueryData<NativeMessageDetail[]>(nativeMessageDetailsKey(platform, variant), (current) =>
        current?.map((item) => (item.language_code === saved.language_code ? saved : item)),
      ),
  })
}

export function useRetranslateNativeMessageDetail(platform: Platform, variant: Variant) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (languageCode: string) =>
      api.post<NativeMessageDetail>(
        `/admin/app-patch-policy/${platform}/native-version/messages/retranslate?variant=${variant}`,
        { language_code: languageCode },
      ),
    onSuccess: (saved) =>
      queryClient.setQueryData<NativeMessageDetail[]>(nativeMessageDetailsKey(platform, variant), (current) =>
        current?.map((item) => (item.language_code === saved.language_code ? saved : item)),
      ),
  })
}

export function useTranslateNativeDraft(platform: Platform, variant: Variant) {
  return useMutation({
    mutationFn: ({ title, message }: { title: string; message: string }) =>
      api.post<{ queued: boolean }>(
        `/admin/app-patch-policy/${platform}/native-version/messages/translate-draft?variant=${variant}`,
        { title, message },
      ),
  })
}

/**
 * Scoped to one platform on purpose: Shorebird numbers patches per release across both platforms,
 * so `shorebird patch -p ios -p android` leaves an iOS-only patch 4 and an Android-only patch 5 on
 * the same release. Offering the unfiltered list would let staff pick a number with no artifact to
 * serve on the platform being promoted.
 */
export function useShorebirdReleasePatches(releaseId: number | null, platform: Platform | null) {
  return useQuery({
    queryKey: ['shorebird-release-patches', releaseId, platform],
    queryFn: () =>
      api.get<ShorebirdReleasePatch[]>(
        `/admin/app-patch-policy/releases/${releaseId}/patches?platform=${platform}`,
      ),
    enabled: releaseId !== null && platform !== null,
  })
}

/**
 * The mandatory-patch picker on the standalone platform cards — every patch,
 * across every release, currently live on stable for that platform. Only
 * ever offers what Save's server-side guard (PatchIsLiveOnStable) would
 * accept: picking one here can't fail with "not live on stable" the way
 * freely typing a number into the manual fallback input can.
 */
export interface StablePatch {
  release_version: string
  patch_number: number
  patch_size_bytes: number | null
}

export function useStablePatches(platform: Platform | null) {
  return useQuery({
    queryKey: stablePatchesKey(platform),
    queryFn: () => api.get<StablePatch[]>(`/admin/app-patch-policy/stable-patches?platform=${platform}`),
    enabled: platform !== null,
  })
}
